if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    // Tracks gift-with-purchase adds currently in flight (keyed by
    // "<mainVariantId>-><giftVariantId>"), so that two near-simultaneous
    // submissions for the same product within this tab (e.g. a double
    // click before the button's disabled state takes effect, or two
    // different add-to-cart entry points firing close together) can't
    // both pass the "does the gift already exist" check and add it twice.
    static giftAddsInFlight = new Set();

    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        // Gift-with-purchase: if this product has a linked gift (see the
        // data-gift-variant-id attribute, rendered from the
        // custom.produto_oferta metafield), add it in the same submission
        // so the customer never has to add it manually.
        let finalData = data;
        const giftVariantId = this.dataset.giftVariantId;

        if (giftVariantId) {
          const giftKey = `${data.variant_id}->${giftVariantId}`;

          // Re-fetch the cart instead of reusing oldCartData (fetched
          // before this submission started) — oldCartData can be stale if
          // another add-to-cart entry point for the same product (e.g.
          // quick add + sticky bar) completed in between. Combined with
          // the in-flight lock below, this closes most of the race window;
          // gift-with-purchase.js's dedupe pass is the final safety net for
          // whatever (rare) overlap still slips through.
          const giftCheckResponse = await fetch(`${theme.routes.cart}.js`);
          const giftCheckCart = giftCheckResponse.ok ? await giftCheckResponse.json() : oldCartData;

          const alreadyHasGift = giftCheckCart.items.some((item) => (
            item.properties
            && String(item.properties._gift_of) === String(data.variant_id)
          ));

          if (!alreadyHasGift && !ProductForm.giftAddsInFlight.has(giftKey)) {
            ProductForm.giftAddsInFlight.add(giftKey);

            const giftFormData = new FormData();
            giftFormData.append('id', giftVariantId);
            giftFormData.append('quantity', 1);
            giftFormData.append('properties[_gift]', 'true');
            giftFormData.append('properties[_gift_of]', data.variant_id);
            giftFormData.append('sections_url', window.location.pathname);
            giftFormData.append('sections', sections);

            try {
              const giftResponse = await fetch(theme.routes.cartAdd, {
                method: 'POST',
                headers: fetchRequestOpts.headers,
                body: giftFormData
              });
              // A failed gift add shouldn't block the main item the
              // customer actually asked for — fall back to `data` silently.
              if (giftResponse.ok) finalData = await giftResponse.json();
            } catch (giftError) {
              finalData = data;
            } finally {
              ProductForm.giftAddsInFlight.delete(giftKey);
            }
          }
        }

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(finalData);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                finalData,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
